export const LOCATION_GOAL = 40;

export const GRADE_OPTIONS = [
  'K', '1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th',
];

export const GRADE_CHART_LABELS = ['K', '1', '2', '3', '4', '5', '6', '7', '8'];

export const SPORT_ALIASES = {
  soccer: 'Soccer',
  flag_football: 'Flag Football',
  flagfootball: 'Flag Football',
  cheer: 'Cheer',
  cheerleading: 'Cheer',
  basketball: 'Basketball',
  'b-ball': 'Basketball',
  football: 'Flag Football',
};

export const normalizeSport = (sport) => {
  if (!sport) return sport;
  const key = sport.trim().toLowerCase().replace(/[_-]/g, ' ');
  return SPORT_ALIASES[key] || SPORT_ALIASES[sport.trim().toLowerCase()] || sport;
};

const GRADE_ALIASES = {
  k: 'K',
  kindergarten: 'K',
  kindergarden: 'K',
  '0': 'K',
  '1': '1st',
  '1st': '1st',
  first: '1st',
  '1st grade': '1st',
  '2': '2nd',
  '2nd': '2nd',
  second: '2nd',
  '2nd grade': '2nd',
  '3': '3rd',
  '3rd': '3rd',
  third: '3rd',
  '3rd grade': '3rd',
  '4': '4th',
  '4th': '4th',
  fourth: '4th',
  '4th grade': '4th',
  '5': '5th',
  '5th': '5th',
  fifth: '5th',
  '5th grade': '5th',
  '6': '6th',
  '6th': '6th',
  sixth: '6th',
  '6th grade': '6th',
  '7': '7th',
  '7th': '7th',
  seventh: '7th',
  '7th grade': '7th',
  '8': '8th',
  '8th': '8th',
  eighth: '8th',
  '8th grade': '8th',
};

export const normalizeLocationKey = (name) => {
  if (!name) return '';
  return String(name)
    .toLowerCase()
    .replace(/,?\s*(md|maryland)$/i, '')
    .replace(/\s+/g, ' ')
    .trim();
};

export const findMatchingLocation = (memberLocation, locations = []) => {
  if (!memberLocation) return null;
  const memberKey = normalizeLocationKey(memberLocation);

  return (
    locations.find((loc) => {
      const locKey = normalizeLocationKey(loc.name);
      return (
        locKey === memberKey ||
        memberKey.includes(locKey) ||
        locKey.includes(memberKey)
      );
    }) || null
  );
};

export const parseDate = (date) => {
  if (!date) return null;
  if (date.toDate) return date.toDate();

  if (typeof date === 'string' && date.match(/^\d{2}-\d{2}-\d{4}$/)) {
    const [month, day, year] = date.split('-');
    const parsed = new Date(`${year}-${month}-${day}`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const parsed = new Date(date);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getAgeFromDob = (dob) => {
  if (!dob) return null;
  const birthDate = new Date(dob);
  if (isNaN(birthDate.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
  return age;
};

const ageToGrade = (age) => {
  if (age === null || age === undefined) return null;
  if (age <= 5) return 'K';
  if (age === 6) return '1st';
  if (age === 7) return '2nd';
  if (age === 8) return '3rd';
  if (age === 9) return '4th';
  if (age === 10) return '5th';
  if (age === 11) return '6th';
  if (age === 12) return '7th';
  if (age >= 13) return '8th';
  return null;
};

export const getStudentGrade = (student) => {
  const raw = student?.grade || student?.studentGrade || '';
  if (raw) {
    const normalized = String(raw).toLowerCase().trim();
    return GRADE_ALIASES[normalized] || raw;
  }

  const gradeBand = student?.gradeBand || '';
  if (gradeBand) {
    if (gradeBand.includes('Kindergarten') || gradeBand.includes('K/1st') || gradeBand.includes('K – 1')) return 'K';
    if (gradeBand.includes('2nd') || gradeBand.includes('2 – 3')) return '2nd';
    if (gradeBand.includes('4th') || gradeBand.includes('4 – 5')) return '4th';
    if (gradeBand.includes('Middle School') || gradeBand.includes('6 – 8')) return '6th';
  }

  const ageFromDob = getAgeFromDob(student?.dob);
  if (ageFromDob !== null) {
    const grade = ageToGrade(ageFromDob);
    if (grade) return grade;
  }

  return 'N/A';
};

export const getPaymentStatus = (member) => {
  const status = String(member?.paymentStatus || '').toLowerCase();
  const membershipType = String(member?.membershipType || '').toLowerCase();

  if (
    member?.isPaidMember ||
    status.includes('paid') ||
    status === 'active' ||
    status === 'completed' ||
    membershipType === 'paid'
  ) {
    return 'Paid';
  }

  if (
    status.includes('pending') ||
    status.includes('past due') ||
    status === 'past due' ||
    status === 'canceled'
  ) {
    return 'Pending';
  }

  return 'Unpaid';
};

export const getRegistrationStatus = (member) => {
  const status = String(member?.registrationStatus || '').toLowerCase();

  if (status === 'active') return 'Active';
  if (status === 'pending') return 'Pending';
  if (status === 'inactive') return 'Inactive';

  const paymentStatus = getPaymentStatus(member);
  if (paymentStatus === 'Paid') return 'Active';
  if (paymentStatus === 'Pending') return 'Pending';
  return 'Inactive';
};

export const getMembershipTypeLabel = (member) => {
  const membershipType = String(member?.membershipType || '').toLowerCase();
  const paymentStatus = String(member?.paymentStatus || '').toLowerCase();
  const registrationPlan = String(member?.registrationPlan || '').toLowerCase();

  if (membershipType.includes('monthly') || paymentStatus.includes('monthly')) {
    return 'Monthly';
  }
  if (membershipType.includes('seasonal') || paymentStatus.includes('seasonal')) {
    return 'Seasonal';
  }
  if (membershipType.includes('yearly')) return 'Yearly';
  if (membershipType.includes('one')) return 'Seasonal';

  if (registrationPlan.includes('monthly')) return 'Monthly';
  if (registrationPlan.includes('one')) return 'Seasonal';

  if (member?.membershipType) {
    const label = String(member.membershipType);
    return label.charAt(0).toUpperCase() + label.slice(1);
  }

  return 'N/A';
};

export const formatSourceLabel = (source) => {
  if (!source) return 'N/A';
  const map = {
    web: 'Web',
    mobile: 'Mobile',
    admin: 'Admin',
    interest: 'Interest Form',
    referral: 'Referral',
  };
  return map[String(source).toLowerCase()] || source;
};

export const expandMembersToStudentRows = (members = []) => {
  const rows = [];

  members.forEach((member) => {
    const students = member.students?.length ? member.students : [null];

    students.forEach((student, index) => {
      const studentName = student
        ? `${student.firstName || ''} ${student.lastName || ''}`.trim() || 'N/A'
        : 'N/A';

      rows.push({
        id: `${member.id}-${index}`,
        memberId: member.id,
        studentIndex: index,
        student,
        studentName,
        grade: student ? getStudentGrade(student) : 'N/A',
        gradeBand: student?.gradeBand || 'N/A',
        parentName: `${member.firstName || ''} ${member.lastName || ''}`.trim(),
        phone: member.phone || '',
        email: member.email || '',
        location: member.location || 'N/A',
        sport: normalizeSport(member.sport) || 'N/A',
        membershipType: getMembershipTypeLabel(member),
        paymentStatus: getPaymentStatus(member),
        registrationStatus: getRegistrationStatus(member),
        source: formatSourceLabel(member.registrationSource),
        sourceRaw: member.registrationSource || '',
        dateRegistered: member.createdAt,
        lastContacted: member.lastContacted || null,
        followUpNeeded: Boolean(member.followUpNeeded),
        followUpDate: member.followUpDate || null,
        followUpAssignedTo: member.followUpAssignedTo || '',
        followUpNotes: member.followUpNotes || '',
        member,
      });
    });
  });

  return rows;
};

export const getUniqueFilterValues = (members = [], studentRows = []) => {
  const sports = new Set();
  const gradeBands = new Set();
  const membershipTypes = new Set();
  const sources = new Set();

  members.forEach((member) => {
    if (member.sport) sports.add(normalizeSport(member.sport));
    membershipTypes.add(getMembershipTypeLabel(member));
    if (member.registrationSource) sources.add(formatSourceLabel(member.registrationSource));
    (member.students || []).forEach((student) => {
      if (student?.gradeBand) gradeBands.add(student.gradeBand);
    });
  });

  return {
    sports: [...sports].sort(),
    gradeBands: [...gradeBands].sort(),
    membershipTypes: [...membershipTypes].filter((v) => v !== 'N/A').sort(),
    sources: [...sources].sort(),
  };
};

export const matchesDateRange = (dateValue, startDate, endDate) => {
  if (!startDate && !endDate) return true;
  const date = parseDate(dateValue);
  if (!date) return false;

  if (startDate) {
    const start = parseDate(startDate);
    if (start && date < start) return false;
  }

  if (endDate) {
    const end = parseDate(endDate);
    if (end) {
      const endOfDay = new Date(end);
      endOfDay.setHours(23, 59, 59, 999);
      if (date > endOfDay) return false;
    }
  }

  return true;
};

export const filterStudentRows = (rows = [], searchTerm = '', filters = {}) => {
  const search = searchTerm.toLowerCase().trim();

  return rows.filter((row) => {
    const matchesSearch =
      !search ||
      row.studentName.toLowerCase().includes(search) ||
      row.parentName.toLowerCase().includes(search) ||
      row.email.toLowerCase().includes(search) ||
      row.phone.includes(search) ||
      row.location.toLowerCase().includes(search) ||
      row.sport.toLowerCase().includes(search);

    const matchesLocation =
      filters.location === 'all' ||
      normalizeLocationKey(row.location) === normalizeLocationKey(filters.location) ||
      row.location === filters.location;

    const matchesGrade =
      filters.grade === 'all' || row.grade === filters.grade;

    const matchesGradeBand =
      filters.gradeBand === 'all' || row.gradeBand === filters.gradeBand;

    const matchesSport =
      filters.sport === 'all' || row.sport === filters.sport;

    const matchesMembershipType =
      filters.membershipType === 'all' ||
      row.membershipType === filters.membershipType;

    const matchesPaymentStatus =
      filters.paymentStatus === 'all' ||
      row.paymentStatus === filters.paymentStatus;

    const matchesRegistrationStatus =
      filters.registrationStatus === 'all' ||
      row.registrationStatus === filters.registrationStatus;

    const matchesSource =
      filters.source === 'all' ||
      row.sourceRaw === filters.source ||
      row.source === filters.source;

    const matchesDate = !filters.dateRegistered || (() => {
      const rowDate = parseDate(row.dateRegistered);
      const filterDate = parseDate(filters.dateRegistered);
      if (!rowDate || !filterDate) return false;
      return (
        rowDate.getFullYear() === filterDate.getFullYear() &&
        rowDate.getMonth() === filterDate.getMonth() &&
        rowDate.getDate() === filterDate.getDate()
      );
    })();

    return (
      matchesSearch &&
      matchesLocation &&
      matchesGrade &&
      matchesGradeBand &&
      matchesSport &&
      matchesMembershipType &&
      matchesPaymentStatus &&
      matchesRegistrationStatus &&
      matchesSource &&
      matchesDate
    );
  });
};

export const computeLocationStats = (members = [], locations = []) => {
  const locationNames = locations.map((loc) => loc.name);

  const statsByLocation = locationNames.reduce((acc, name) => {
    acc[name] = {
      location: name,
      students: 0,
      goal: LOCATION_GOAL,
      active: 0,
      pending: 0,
      unpaid: 0,
      monthly: 0,
      seasonal: 0,
      gradeCounts: GRADE_OPTIONS.reduce((grades, grade) => {
        grades[grade] = 0;
        return grades;
      }, {}),
    };
    return acc;
  }, {});

  members.forEach((member) => {
    const matchedLocation = findMatchingLocation(member.location, locations);
    if (!matchedLocation) return;
    const locationName = matchedLocation.name;
    if (!locationName || !statsByLocation[locationName]) return;

    const stats = statsByLocation[locationName];
    const paymentStatus = getPaymentStatus(member);
    const registrationStatus = getRegistrationStatus(member);
    const membershipType = getMembershipTypeLabel(member);
    const studentCount = member.students?.length || 1;

    stats.students += studentCount;

    if (registrationStatus === 'Active') stats.active += studentCount;
    if (registrationStatus === 'Pending') stats.pending += studentCount;
    if (paymentStatus === 'Unpaid') stats.unpaid += studentCount;
    if (membershipType === 'Monthly') stats.monthly += studentCount;
    if (membershipType === 'Seasonal') stats.seasonal += studentCount;

    (member.students || []).forEach((student) => {
      const grade = getStudentGrade(student);
      if (stats.gradeCounts[grade] !== undefined) {
        stats.gradeCounts[grade] += 1;
      }
    });
  });

  return Object.values(statsByLocation).map((stats) => ({
    ...stats,
    percentToGoal: Math.round((stats.students / stats.goal) * 100),
  }));
};

export const computeSitePerformanceTotals = (locationStats = []) => {
  const totals = locationStats.reduce(
    (acc, stat) => {
      acc.students += stat.students;
      acc.goal += stat.goal;
      acc.active += stat.active;
      acc.pending += stat.pending;
      acc.monthly += stat.monthly;
      acc.seasonal += stat.seasonal;
      return acc;
    },
    {
      location: 'TOTAL',
      students: 0,
      goal: 0,
      active: 0,
      pending: 0,
      monthly: 0,
      seasonal: 0,
      percentToGoal: 0,
    }
  );

  totals.percentToGoal = totals.goal
    ? Math.round((totals.students / totals.goal) * 100)
    : 0;

  return totals;
};

export const computeGradeDistribution = (studentRows = [], locationFilter = 'all') => {
  const counts = GRADE_CHART_LABELS.reduce((acc, grade) => {
    acc[grade] = 0;
    return acc;
  }, {});

  const filteredRows =
    locationFilter === 'all'
      ? studentRows
      : studentRows.filter(
          (row) =>
            normalizeLocationKey(row.location) ===
            normalizeLocationKey(locationFilter)
        );

  filteredRows.forEach((row) => {
    const grade = row.grade;
    const chartKey =
      grade === 'K'
        ? 'K'
        : grade?.replace(/(st|nd|rd|th)/, '') || null;

    if (chartKey && counts[chartKey] !== undefined) {
      counts[chartKey] += 1;
    }
  });

  return counts;
};

export const computeMembershipBreakdown = (members = []) => {
  let monthly = 0;
  let seasonal = 0;
  let unpaidPending = 0;

  members.forEach((member) => {
    const membershipType = getMembershipTypeLabel(member);
    const paymentStatus = getPaymentStatus(member);
    const studentCount = member.students?.length || 1;

    if (membershipType === 'Monthly' && paymentStatus === 'Paid') {
      monthly += studentCount;
    } else if (membershipType === 'Seasonal' && paymentStatus === 'Paid') {
      seasonal += studentCount;
    } else if (paymentStatus !== 'Paid') {
      unpaidPending += studentCount;
    }
  });

  const total = monthly + seasonal + unpaidPending || 1;

  return {
    monthly,
    seasonal,
    unpaidPending,
    total: monthly + seasonal + unpaidPending,
    monthlyPercent: Math.round((monthly / total) * 100),
    seasonalPercent: Math.round((seasonal / total) * 100),
    unpaidPendingPercent: Math.round((unpaidPending / total) * 100),
  };
};

export const isContactedThisWeek = (lastContacted) => {
  const date = parseDate(lastContacted);
  if (!date) return false;

  const weekAgo = new Date();
  weekAgo.setDate(weekAgo.getDate() - 7);
  return date >= weekAgo;
};

export const getFollowUpItems = (members = []) => {
  return members
    .filter((member) => member.followUpNeeded)
    .flatMap((member) => {
      const students = member.students?.length
        ? member.students
        : [{ firstName: 'N/A', lastName: '' }];

      return students.map((student, index) => ({
        id: `${member.id}-followup-${index}`,
        memberId: member.id,
        studentName: `${student.firstName || ''} ${student.lastName || ''}`.trim() || 'N/A',
        location: member.location || 'N/A',
        followUpDate: member.followUpDate,
        assignedTo: member.followUpAssignedTo || 'Unassigned',
        status: member.lastContacted ? 'Contacted' : 'Follow-up',
        notes: member.followUpNotes || '',
        member,
      }));
    })
    .sort((a, b) => {
      const dateA = parseDate(a.followUpDate);
      const dateB = parseDate(b.followUpDate);
      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;
      return dateA - dateB;
    });
};

export const getLeadsCount = (parents = [], members = []) => {
  const memberEmails = new Set(
    members.map((member) => member.email?.toLowerCase()).filter(Boolean)
  );

  return parents.filter((parent) => {
    const isLead =
      parent.isLead ||
      parent.isInterested ||
      parent.status === 'lead' ||
      parent.status === 'interested' ||
      parent.registrationStatus === 'lead';

    const notYetMember =
      parent.email && !memberEmails.has(parent.email.toLowerCase());

    return isLead || (notYetMember && parent.students?.length);
  }).length;
};

export const getContactedThisWeekCount = (members = []) =>
  members.filter((member) => isContactedThisWeek(member.lastContacted)).length;
